import { Tooltip } from 'antd';
import Table from 'common/table/legacy-table';
import { DynamicActions } from 'common/toolbar';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Flex, Icon, notification } from 'ui';
import { withRouter } from 'utils/withRouter';
import { urlSigns } from '../../utils/app-configs';

const AlertIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts['1']};
  font-size: 1.2rem;
  padding-top: 18px;
  padding-right: 25px;
  width: 18px;
`;

const TicketIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts['3']};
  font-size: 1.2rem;
  padding-top: 18px;
  padding-right: 25px;
  width: 18px;
`;

const IconPlaceholder = styled.span`
  width: 18px;
`;

const OrderIcon = styled(Icon)`
  color: ${p => p.orderType === 'main' && '#ff9600'};
  cursor: pointer;
  padding-top: 18px;
  padding-right: 20px;
  width: 18px;
  font-size: 1.2rem;
  vertical-align: bottom;
`;

const DynamicActionsTable = (props) => {
  const {
    data,
    columns,
    hasDynamicActions = true,
    intl,
    children,
    hasFixedColumns = false,
    history,
    highlightedKeys,
    setSelectedOrderIds,
    isPlanningBoard,
    addedBlockIds,
    hasCheckboxAndIcons = true,
    match,
  } = props;
  const [selectedIdsState, setSelectedIdsState] = useState([]);
  const [availableActionsState, setAvailableActionsState] = useState({});

  useEffect(() => {
    setSelectedIdsState([]);
    if (setSelectedOrderIds) {
      setSelectedOrderIds([]);
    }
    setAvailableActionsState({});
  }, [match?.params?.sort, match?.params?.filters]);

  const getCheckboxProps = (record) => {
    if (record.type === 'historicalorder') {
      return { disabled: true };
    }

    return {
      disabled: !record?.actions?.length,
      style: {
        minWidth: 70,
      },
    };
  };

  const handleIconClick = (e, redirectUri) => {
    e.stopPropagation();
    history.replace(`/${redirectUri}`);
  };

  const navigateToDocument = (url) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank');
      return;
    }
    window.open(`http://${url}`, '_blank');
  };

  const getColumns = () => [
    {
      width: '1px',
      fixed: hasFixedColumns,
      render: (text, record) => {
        if (record.type === 'historicalorder') {
          return (
            <Flex jcsa marginLeft={-74} marginTop={3}>
              <Tooltip
                placement='left'
                title={intl.formatMessage({ id: 'timeline.HISTORICAL_ORDER' })}
              >
                {/* There needs to be a div tag for the tooltip to work */}
                <div>
                  <Icon type='history' />
                </div>
              </Tooltip>
            </Flex>
          );
        }
        return (
          <Flex jcsa marginLeft={-74} marginTop={3}>
            <Tooltip placement='left' title={intl.formatMessage({ id: 'timeline.NO_ACTIONS' })}>
              {!record?.actions?.length &&
                <div>
                  <Icon type='ban' />
                </div>
              }
            </Tooltip>
          </Flex>
        );
      },
    },
    ...columns,
    {
      render: (text, record) =>
        <Flex jcsa>
          {record.hasAlert || (record.indicators && record.indicators.hasAlerts) ?
            <AlertIcon type='exclamation-circle' />
            :
            <IconPlaceholder />
          }
          {record.hasTicket || (record.indicators && record.indicators.hasTickets) ?
            <TicketIcon
              type='comment'
              onClick={(e) => {
                e.stopPropagation();
                // eslint-disable-next-line max-len
                history.push(`/${record.uri}/tickets/object/${record.id}/filters/object${urlSigns.keyValueSeparator}${record.id}/view/ticket_none`);
              }}
            />
            :
            <IconPlaceholder />
          }
          {record.indicators && record.indicators.hasVisitReports ?
            <TicketIcon
              light={true}
              type='file-alt'
              onClick={(e) => {
                e.stopPropagation();
                navigateToDocument(record.indicators.latestVisitReportUri);
              }}
            />
            :
            <IconPlaceholder />
          }
          {record.indicators && (record.indicators.isSubOrder || record.indicators.isMainOrder) ?
            <Tooltip
              placement='left'
              title={intl.formatMessage({ id: 'order.fieldEngineer.LINKED_ORDERS_TOOLTIP' })}
            >
              <div>
                <OrderIcon
                  orderType={record.indicators.isMainOrder && 'main'}
                  type={record.indicators.isSubOrder ? 'link' : 'crown'}
                  onClick={e => handleIconClick(e, record.indicators.redirectUri)}
                />
              </div>
            </Tooltip>
            :
            <IconPlaceholder />
          }
        </Flex>,
      width: '150px',
    },
  ];

  const checkLimiters = (action, selectedRows) => {
    if (action.limiter) {
      const limiters = action.limiter.split(',');

      return limiters.every((limiter) => {
        const actionForFirstRow =
          selectedRows &&
          selectedRows.length &&
          selectedRows[0].actions?.find(r => r.id === action.id);
        const expectedValueForFirstRow = actionForFirstRow && actionForFirstRow.payload[limiter];

        return (
          selectedRows &&
          selectedRows.length &&
          selectedRows.every((row) => {
            const actionForCurrentRow = row.actions && row.actions?.find(r => r.id === action.id);
            const actualValue = actionForCurrentRow && actionForCurrentRow.payload[limiter];
            return actualValue === expectedValueForFirstRow;
          })
        );
      });
    }
    return true;
  };

  const handleRowSelect = (selectedIds) => {
    const selectedRows = data?.filter(r => selectedIds.includes(r.id));
    const allActions = selectedRows.reduce((actions, row) => [...actions, ...row.actions], []);

    setSelectedOrderIds(selectedIds);

    // If the selectedRows consist of orders & notifications/calls,
    // show warning notification.
    if (
      selectedRows.some(r => r.type === 'order') &&
      selectedRows.some(r => r.type === 'call' || r.type === 'notification')
    ) {
      notification.warning({
        message: intl.formatMessage({
          id: 'timeline.toolbar.MSG_PROCESSED_TOGETHER',
        }),
        placement: 'top',
      });
      setSelectedIdsState(selectedIds);
      setAvailableActionsState({});
      return;
    }

    // Create a object with all availble actions.
    // key: actionId, value: array of payload to pass
    const availableActions = allActions.reduce((actions, action) => {
      // All rows selected should have the action available, or the button will dissapear
      if (selectedRows.every(row => row.actions?.find(r => r.id === action.id))) {
        // also, check if there is a limiter & if the limiter applies to all rows.
        if (checkLimiters(action, selectedRows)) {
          return {
            ...actions,
            [action.id]: actions[action.id]
              ? [...actions[action.id], action.payload]
              : [action.payload],
          };
        }
      }
      return actions;
    }, {});
    setSelectedIdsState(selectedIds);
    setAvailableActionsState(availableActions);
  };

  useEffect(() => {
    if (addedBlockIds) {
      setSelectedIdsState(state => state?.filter(s => !addedBlockIds?.find(i => i === s)));
      handleRowSelect(selectedIdsState?.filter(s => !addedBlockIds?.find(i => i === s)));
    }
  }, [addedBlockIds]);

  if (hasDynamicActions) {
    return (
      <>
        {children({
          renderDynamicActions: () =>
            <DynamicActions
              reload={() => {
                setSelectedIdsState([]);
                setAvailableActionsState({});
              }}
              showAsQuickAccess={true}
              availableActions={availableActionsState}
              isPlanningBoard={isPlanningBoard}
            />,
          renderTable: () =>
            <Table
              {...props}
              columns={!hasCheckboxAndIcons ? columns : getColumns()}
              selectedRowKeys={selectedIdsState}
              onRowSelect={handleRowSelect}
              getCheckboxProps={record => getCheckboxProps(record)}
              highlightedKeys={highlightedKeys}
              isPlanningBoard={isPlanningBoard}
            />
          ,
        })}
      </>
    );
  }
  return (
    <Table
      {...props}
      isPlanningBoard={isPlanningBoard}
      columns={getColumns()}
      getCheckboxProps={record => getCheckboxProps(record)}
      highlightedKeys={highlightedKeys}
    />
  );
};

DynamicActionsTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  hasDynamicActions: PropTypes.bool,
  intl: PropTypes.object,
  children: PropTypes.func,
  hasFixedColumns: PropTypes.bool,
  history: PropTypes.object,
  highlightedKeys: PropTypes.array,
  setSelectedOrderIds: PropTypes.func,
  isPlanningBoard: PropTypes.bool,
  addedBlockIds: PropTypes.array,
  hasCheckboxAndIcons: PropTypes.bool,
  match: PropTypes.object,
};

export default withRouter(injectIntl(DynamicActionsTable));
